import {
  MbmProject,
  MbmProjectHistoryEntry,
  MbmProjectInsertInput,
  MbmProjectUpdateInput,
} from '../types/mbm-project.types';
import { MbmProjectFormValues } from '../schemas/mbm-project-form-schema';

/** Reads the stored history; a missing or corrupt value is treated as "no history yet". */
export const parseMbmProjectHistory = (raw?: string): MbmProjectHistoryEntry[] => {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MbmProjectHistoryEntry[]) : [];
  } catch {
    return [];
  }
};

const appendHistory = (
  raw: string | undefined,
  values: Pick<
    MbmProjectHistoryEntry,
    'ProjectId' | 'ProjectName' | 'CustomerId' | 'CustomerName' | 'Status'
  >,
  now: Date
) =>
  JSON.stringify([
    ...parseMbmProjectHistory(raw),
    {
      ProjectId: values.ProjectId,
      ProjectName: values.ProjectName,
      CustomerId: values.CustomerId,
      CustomerName: values.CustomerName,
      Status: values.Status,
      CreateDate: now.toISOString(),
    },
  ]);

const trimmed = (values: MbmProjectFormValues): MbmProjectFormValues => ({
  ProjectId: values.ProjectId.trim(),
  ProjectName: values.ProjectName.trim(),
  CustomerId: values.CustomerId.trim(),
  CustomerName: values.CustomerName.trim(),
  Status: values.Status,
});

/** A new project starts its history with a snapshot of itself, as the legacy upsert did. */
export const buildMbmProjectInsertInput = (
  formValues: MbmProjectFormValues,
  now = new Date()
): MbmProjectInsertInput => {
  const values = trimmed(formValues);
  return {
    ...values,
    MbmProjectHistory: appendHistory(undefined, values, now),
  };
};

/**
 * Only the name, customer name and status can change: `ProjectId` and `CustomerId` are the MBM
 * keys that timesheet entries store, so they stay fixed. Returns `null` when nothing changed so
 * the history is not padded with identical snapshots.
 */
export const buildMbmProjectUpdateInput = (
  current: MbmProject,
  formValues: MbmProjectFormValues,
  now = new Date()
): MbmProjectUpdateInput | null => {
  const values = trimmed(formValues);
  const unchanged =
    values.ProjectName === current.ProjectName &&
    values.CustomerName === current.CustomerName &&
    values.Status === (current.Status ?? '');
  if (unchanged) return null;

  return {
    ProjectName: values.ProjectName,
    CustomerName: values.CustomerName,
    Status: values.Status,
    // Identity comes from the stored record, not the form, so it can never drift from the keys.
    MbmProjectHistory: appendHistory(
      current.MbmProjectHistory,
      { ...values, ProjectId: current.ProjectId, CustomerId: current.CustomerId },
      now
    ),
  };
};
